'use client';
 
import {
  useCallback,
  useMemo,
  useRef,
  useEffect,
  useState,
  DependencyList,
  MutableRefObject,
  lazy,
  ComponentType,
  ReactElement,
  Suspense,
} from 'react';

import { logger } from '../utils';
import { LoadingState } from '../components/loading-state';

/**
 * Enhanced performance hooks for React components
 * Provides advanced optimization patterns beyond standard React hooks
 */

/**
 * Enhanced useCallback that tracks dependency changes in development
 * @param callback - The callback function
 * @param deps - Dependency array
 * @returns Memoized callback
 */
export function useOptimizedCallback<T extends (...args: unknown[]) => unknown>(
  callback: T,
  deps: DependencyList
): T {
  // For React 19 compatibility: Use useCallback with proper dependency handling
  // Use a ref to ensure callback is always current without dependency on callback itself
  const callbackRef = useRef(callback);

  // Update ref on every render to ensure it's current
  useEffect(() => {
    callbackRef.current = callback;
  });

  // Memoize with provided dependencies, but use ref for stable callback access
  const memoizedCallback = useMemo(
    () => ((...args: Parameters<T>) => callbackRef.current(...args)) as T,
    // Use spread operator to make deps explicit array for ESLint
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps spread into new array; callbackRef provides stable access
    [...deps]
  );

  // Development-only dependency tracking
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      logger.debug('[useOptimizedCallback] Dependencies changed, callback recreated');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: track only user-provided deps, not logger
  }, [...deps]);

  return memoizedCallback;
}

/**
 * useMemo with deep comparison of dependencies
 * @param factory - Factory function
 * @param deps - Dependencies to deep compare
 * @returns Memoized value
 */
export function useDeepMemo<T>(factory: () => T, deps: DependencyList): T {
  const ref = useRef<{ deps: DependencyList; value: T } | null>(null);

  if (!ref.current || !deepArrayEqual(deps, ref.current.deps)) {
    ref.current = { deps, value: factory() };
  }

  return ref.current.value;
}

/**
 * Hook for lazy loading components with error boundary
 * @param importFn - Dynamic import function
 * @param fallback - Loading fallback
 * @returns Object with Component and loading state
 */
export function useLazyComponent<T extends ComponentType>(
  importFn: () => Promise<{ default: T }>,
  fallback?: ReactElement
) {
  const [Component, setComponent] = useState<React.ComponentType<Record<string, unknown>>>();
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const loadComponent = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Pre-test the import function to catch errors
        await importFn();

        const LazyComponent = lazy(importFn);
        if (mounted) {
          setComponent(
            () => LazyComponent as unknown as React.ComponentType<Record<string, unknown>>
          );
          setIsLoading(false);
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err : new Error('Failed to load component'));
          setIsLoading(false);
        }
      }
    };

    loadComponent();

    return () => {
      mounted = false;
    };
  }, [importFn]);

  const WrappedComponent = useMemo(() => {
    if (error) {
      return (_props: Record<string, unknown>) => (
        <div data-testid="lazy-error">Error loading component: {error.message}</div>
      );
    }

    if (!Component || isLoading) {
      return (_props: Record<string, unknown>) =>
        fallback || <LoadingState preset="kpiCard" data-testid="lazy-loading" />;
    }

    const Wrapper = (props: Record<string, unknown>): ReactElement => (
      <Suspense fallback={fallback || <LoadingState preset="kpiCard" data-testid="lazy-loading" />}>
        <Component {...props} />
      </Suspense>
    );

    return Wrapper;
  }, [Component, fallback, error, isLoading]);

  return {
    Component: WrappedComponent,
    error,
    isLoading,
    retry: () => {
      setError(null);
      setIsLoading(true);

      const loadComponentRetry = async () => {
        try {
          // Pre-test the import function again with explicit .catch() handling
          await importFn().catch((err) => {
            throw err instanceof Error ? err : new Error('Import function failed');
          });

          const LazyComponent = lazy(importFn);
          setComponent(
            () => LazyComponent as unknown as React.ComponentType<Record<string, unknown>>
          );
          setIsLoading(false);
        } catch (err) {
          setError(err instanceof Error ? err : new Error('Failed to load component'));
          setIsLoading(false);
        }
      };

      loadComponentRetry().catch((err) => {
        logger.error('Failed to retry loading component', err);
      });
    },
  };
}

/**
 * Virtual scrolling hook for large lists
 * @param items - Array of items
 * @param containerRef - Reference to scroll container
 * @param itemHeight - Height of each item (can be function for dynamic heights)
 * @param overscan - Number of items to render outside visible area
 */
export function useVirtualScroll<T>(
  items: T[],
  containerRef: MutableRefObject<HTMLElement | null>,
  itemHeight: number | ((index: number) => number),
  overscan: number = 3
) {
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);

  // Calculate item heights
  const getItemHeight = useCallback(
    (index: number) => {
      return typeof itemHeight === 'function' ? itemHeight(index) : itemHeight;
    },
    [itemHeight]
  );

  // Calculate total height and item offsets
  const { totalHeight, itemOffsets } = useMemo(() => {
    let total = 0;
    const offsets: number[] = [];

    for (let i = 0; i < items.length; i++) {
      offsets.push(total);
      total += getItemHeight(i);
    }

    return { totalHeight: total, itemOffsets: offsets };
  }, [items.length, getItemHeight]);

  // Calculate visible range
  const visibleRange = useMemo(() => {
    if (!containerHeight) return { start: 0, end: 0 };

    let start = 0;
    let end = items.length - 1;

    // Find start index
    for (let i = 0; i < itemOffsets.length; i++) {
      if (itemOffsets[i] + getItemHeight(i) > scrollTop) {
        start = Math.max(0, i - overscan);
        break;
      }
    }

    // Find end index
    const viewportEnd = scrollTop + containerHeight;
    for (let i = start; i < itemOffsets.length; i++) {
      if (itemOffsets[i] > viewportEnd) {
        end = Math.min(items.length - 1, i + overscan);
        break;
      }
    }

    return { start, end };
  }, [scrollTop, containerHeight, itemOffsets, items.length, overscan, getItemHeight]);

  // Handle scroll
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      setScrollTop(container.scrollTop);
    };

    const handleResize = () => {
      setContainerHeight(container.clientHeight);
    };

    handleResize(); // Initial measurement
    container.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [containerRef]);

  // Get visible items with their styles
  const visibleItems = useMemo(() => {
    const items: Array<{
      index: number;
      style: React.CSSProperties;
    }> = [];

    for (let i = visibleRange.start; i <= visibleRange.end; i++) {
      items.push({
        index: i,
        style: {
          position: 'absolute',
          top: itemOffsets[i],
          height: getItemHeight(i),
          left: 0,
          right: 0,
        },
      });
    }

    return items;
  }, [visibleRange, itemOffsets, getItemHeight]);

  return {
    totalHeight,
    visibleItems,
    visibleRange,
    scrollToIndex: (index: number, behavior: 'auto' | 'instant' | 'smooth' = 'auto') => {
      const container = containerRef.current;
      if (!container || !itemOffsets[index]) return;

      container.scrollTo({
        top: itemOffsets[index],
        behavior,
      });
    },
  };
}

/**
 * Debug hook to track component render count
 * @param _componentName - Name for logging (unused in production)
 */
export function useRenderCount(_componentName: string): number {
  const renderCount = useRef(0);

  // Increment before rendering so the count is available immediately
  renderCount.current++;

  if (process.env.NODE_ENV !== 'production') {
    if (process.env.NODE_ENV === 'development')
      logger.debug(`[Render Count] ${_componentName}: ${renderCount.current}`);
  }

  return renderCount.current;
}

/**
 * Hook to track why a component re-rendered
 * @param componentName - Component name
 * @param props - Current props
 */
export function useWhyDidYouUpdate<P extends Record<string, unknown>>(
  componentName: string,
  props: P
): void {
  const previousProps = useRef<P | null>(null);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && previousProps.current) {
      const allKeys = Object.keys({ ...previousProps.current, ...props });
      const changedProps: Record<string, { from: unknown; to: unknown }> = {};

      allKeys.forEach((key) => {
        if (!Object.is(previousProps.current![key], props[key])) {
          changedProps[key] = {
            from: previousProps.current![key],
            to: props[key],
          };
        }
      });

      if (Object.keys(changedProps).length > 0) {
        if (process.env.NODE_ENV === 'development')
          logger.debug('[why-did-you-update]', { componentName, changedProps });
      }
    }

    previousProps.current = props;
  });
}

/**
 * Debounced value hook
 * @param value - Value to debounce
 * @param delay - Debounce delay
 */
export function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Throttled value hook
 * @param value - Value to throttle
 * @param delay - Throttle delay
 */
export function useThrottledValue<T>(value: T, delay: number): T {
  const [throttledValue, setThrottledValue] = useState(value);
  const lastRan = useRef(Date.now());

  useEffect(() => {
    const handler = setTimeout(
      () => {
        if (Date.now() - lastRan.current >= delay) {
          setThrottledValue(value);
          lastRan.current = Date.now();
        }
      },
      delay - (Date.now() - lastRan.current)
    );

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return throttledValue;
}

/**
 * Hook for intersection observer (lazy loading)
 * @param ref - Element ref
 * @param options - Intersection observer options
 */
export function useIntersectionObserver(
  ref: MutableRefObject<Element | null>,
  options?: IntersectionObserverInit
): boolean {
  const [isIntersecting, setIntersecting] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => setIntersecting(entry.isIntersecting),
      options
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [ref, options]);

  return isIntersecting;
}

/**
 * Deep array equality check
 */
function deepArrayEqual(a: DependencyList, b: DependencyList): boolean {
  if (a.length !== b.length) return false;

  for (let i = 0; i < a.length; i++) {
    if (!deepEqual(a[i], b[i])) return false;
  }

  return true;
}

/**
 * Deep equality check
 */
function deepEqual(a: unknown, b: unknown): boolean {
  // Handle exact equality (includes null === null, undefined === undefined)
  if (a === b) return true;

  // Handle null/undefined cases explicitly
  if (a === null && b === null) return true;
  if (a === undefined && b === undefined) return true;
  if (a === null || b === null) return false;
  if (a === undefined || b === undefined) return false;

  // Handle different types
  if (typeof a !== typeof b) return false;
  if (typeof a !== 'object') return false;

  // Handle Date objects
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }

  // Handle Arrays
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], b[i])) return false;
    }
    return true;
  }

  // One is array, other is not
  if (Array.isArray(a) || Array.isArray(b)) return false;

  // Handle plain objects
  if (typeof a === 'object' && typeof b === 'object') {
    const keysA = Object.keys(a as Record<string, unknown>);
    const keysB = Object.keys(b as Record<string, unknown>);

    if (keysA.length !== keysB.length) return false;

    for (const key of keysA) {
      if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
      if (!deepEqual((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key]))
        return false;
    }

    return true;
  }

  return false;
}
